import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View, Text, StyleSheet, TextInput, TouchableOpacity,
  ActivityIndicator, Modal, ScrollView, Linking, useWindowDimensions,
  Pressable, Animated, Easing,
} from 'react-native';
import {
  collection, onSnapshot, doc, updateDoc, deleteDoc, query, orderBy,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

/**
 * Live management screen for the `zapee_delivery_partners` collection
 * (the newer rider schema: partnerId, currentLocation, geohash,
 * rejectionStats, services, vehicleType …). Standalone — does not touch
 * the legacy `deliveryPartner` collection used by partnerScreen.tsx.
 */

const COLLECTION = 'zapee_delivery_partners';

const C = {
  primary: '#21808D', primaryLight: '#e8f5f7',
  success: '#10b981', successLight: '#d1fae5',
  warning: '#b45309', warningLight: '#fef9c3',
  danger: '#ef4444', dangerLight: '#fee2e2',
  blue: '#1d4ed8', blueLight: '#dbeafe',
  bg: '#f6f8fb', card: '#ffffff',
  textPrimary: '#1a2332', textMuted: '#64748b', border: '#e2e8f0', rowAlt: '#fbfcfe',
};

const PAGE_SIZE = 10;
const TABLE_MIN_WIDTH = 1400;
const COLS = {
  sn: 0.4,
  rider: 2.2,
  phone: 1.2,
  vehicle: 1.5,
  salary: 1,
  rejections: 0.8,
  updated: 1.4,
  actions: 3.6,
};

const TOGGLES: { f: 'active' | 'available'; label: string; color: string }[] = [
  { f: 'active', label: 'Online', color: '#10b981' },
  { f: 'available', label: 'Available', color: '#b45309' },
];

interface Rider {
  docId: string;
  partnerId?: string;
  name?: string;
  email?: string;
  phone?: string;
  active?: boolean;
  available?: boolean;
  profileCompleted?: boolean;
  vehicleType?: string;
  vehicleNumber?: string;
  salary?: number;
  geohash?: string;
  currentOrderId?: string;
  lastAssignedOrderId?: string;
  rejectionCount?: number;
  services?: string[];
  currentLocation?: { latitude?: number; longitude?: number; updatedAt?: any };
  rejectionStats?: { day?: Record<string, number>; month?: Record<string, number>; year?: Record<string, number> };
  createdAt?: any;
  updatedAt?: any;
  lastAssignedAt?: any;
  lastRejectedAt?: any;
}

function fmtDate(ts: any): string {
  if (!ts) return '—';
  try {
    const d = ts?.toDate ? ts.toDate() : (typeof ts === 'number' ? new Date(ts) : new Date(String(ts)));
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
}

function vehicleEmoji(type?: string): string {
  const t = (type || '').toLowerCase();
  if (t.includes('scooter') || t.includes('bike') || t.includes('motor')) return '🛵';
  if (t.includes('cycle') || t.includes('bicycle')) return '🚲';
  if (t.includes('car')) return '🚗';
  if (t.includes('truck') || t.includes('van')) return '🚚';
  return '🛵';
}

export default function RiderManager() {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [riders, setRiders] = useState<Rider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'available' | 'offline'>('all');
  const [selected, setSelected] = useState<Rider | null>(null);
  const [page, setPage] = useState(1);

  // Editable salary (rider detail drawer)
  const [salaryInput, setSalaryInput] = useState('');
  const [savingSalary, setSavingSalary] = useState(false);

  // Delete confirm + toast (web-safe; Alert.alert buttons don't render on web)
  const [deleteTarget, setDeleteTarget] = useState<Rider | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const toastTimer = useRef<any>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  };

  // Right-drawer slide animation for the rider detail view
  const drawerW = Math.min(isMobile ? width : 520, width);
  const slideX = useRef(new Animated.Value(drawerW)).current;
  useEffect(() => {
    if (selected) {
      slideX.setValue(drawerW);
      Animated.timing(slideX, { toValue: 0, duration: 280, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    }
  }, [selected, drawerW, slideX]);
  const closeDrawer = () => {
    Animated.timing(slideX, { toValue: drawerW, duration: 220, easing: Easing.in(Easing.cubic), useNativeDriver: true })
      .start(() => setSelected(null));
  };

  // Seed the editable salary field whenever a rider drawer opens
  useEffect(() => {
    if (selected) setSalaryInput(String(selected.salary ?? 0));
  }, [selected]);

  // ── Live listener ─────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    let unsub: (() => void) | undefined;
    try {
      unsub = onSnapshot(
        query(collection(db, COLLECTION), orderBy('updatedAt', 'desc')),
        (snap) => {
          const list = snap.docs.map(d => ({ docId: d.id, ...(d.data() as any) })) as Rider[];
          setRiders(list);
          setLoading(false);
          setError(null);
        },
        (e) => {
          // Fall back to an unordered read if the orderBy field is missing on some docs
          console.warn('Ordered riders listener failed, falling back:', e);
          unsub = onSnapshot(
            collection(db, COLLECTION),
            (snap) => {
              setRiders(snap.docs.map(d => ({ docId: d.id, ...(d.data() as any) })) as Rider[]);
              setLoading(false);
              setError(null);
            },
            (err) => { console.error(err); setError('Failed to load riders'); setLoading(false); }
          );
        }
      );
    } catch (e) {
      console.error(e);
      setError('Failed to load riders');
      setLoading(false);
    }
    return () => { if (unsub) unsub(); };
  }, []);

  // ── Stats ─────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = riders.length;
    const active = riders.filter(r => r.active).length;
    const available = riders.filter(r => r.available).length;
    const onOrder = riders.filter(r => !!r.currentOrderId).length;
    return { total, active, available, onOrder };
  }, [riders]);

  // ── Filtering ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const lower = searchQuery.trim().toLowerCase();
    return riders.filter(r => {
      if (statusFilter === 'online' && !r.active) return false;
      if (statusFilter === 'available' && !r.available) return false;
      if (statusFilter === 'offline' && r.active) return false;
      if (!lower) return true;
      return (
        (r.name || '').toLowerCase().includes(lower) ||
        (r.phone || '').includes(lower) ||
        (r.vehicleNumber || '').toLowerCase().includes(lower) ||
        (r.partnerId || '').toLowerCase().includes(lower)
      );
    });
  }, [riders, searchQuery, statusFilter]);

  useEffect(() => { setPage(1); }, [searchQuery, statusFilter, riders.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Keep the open drawer in sync with live snapshot updates
  const liveSelected = selected ? (riders.find(r => r.docId === selected.docId) || selected) : null;

  // Plain partial updateDoc — safe to send a single field (no full-payload rebuild)
  const toggleField = async (rider: Rider, field: 'active' | 'available') => {
    try {
      await updateDoc(doc(db, COLLECTION, rider.docId), { [field]: !rider[field] });
    } catch (e) {
      console.error(`Failed to toggle ${field}:`, e);
      showToast(`Failed to update ${field}`, 'error');
    }
  };

  // Save the editable salary (partial updateDoc — single field only)
  const saveSalary = async (rider: Rider) => {
    setSavingSalary(true);
    try {
      await updateDoc(doc(db, COLLECTION, rider.docId), { salary: Number(salaryInput) || 0 });
      showToast('Salary updated');
    } catch (e) {
      console.error('Failed to update salary:', e);
      showToast('Failed to update salary', 'error');
    } finally {
      setSavingSalary(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, COLLECTION, deleteTarget.docId));
      showToast('Rider deleted');
      setDeleteTarget(null);
    } catch {
      showToast('Failed to delete rider', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const openMap = (rider: Rider) => {
    const lat = rider.currentLocation?.latitude;
    const lon = rider.currentLocation?.longitude;
    if (lat == null || lon == null) { showToast('No location available for this rider.', 'error'); return; }
    Linking.openURL(`https://www.google.com/maps?q=${lat},${lon}`);
  };

  // ── Table row ─────────────────────────────────────────────────
  const Row = ({ item, index }: { item: Rider; index: number }) => {
    const name = item.name || item.email || 'Unnamed Rider';
    return (
      <View style={[tb.row, index % 2 === 1 && { backgroundColor: C.rowAlt }]}>
        {/* # */}
        <View style={[tb.td, { flex: COLS.sn, alignItems: 'center' }]}>
          <Text style={tb.snTxt}>{(safePage - 1) * PAGE_SIZE + index + 1}</Text>
        </View>
        {/* Rider */}
        <View style={[tb.td, { flex: COLS.rider, flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
          <View style={[tb.avatar, { backgroundColor: item.active ? C.primary : '#94a3b8' }]}>
            <Text style={tb.avatarTxt}>{vehicleEmoji(item.vehicleType)}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={tb.name} numberOfLines={1}>{name}</Text>
            <Text style={tb.sub} numberOfLines={1}>
              {item.currentOrderId ? '🔵 On order' : (item.partnerId || item.docId)}
            </Text>
          </View>
        </View>
        {/* Phone */}
        <View style={[tb.td, { flex: COLS.phone }]}>
          <Text style={tb.cellTxt} numberOfLines={1}>{item.phone || '—'}</Text>
        </View>
        {/* Vehicle */}
        <View style={[tb.td, { flex: COLS.vehicle }]}>
          <Text style={tb.cellTxt} numberOfLines={1}>{item.vehicleType || '—'}</Text>
          <Text style={tb.cellSub} numberOfLines={1}>{item.vehicleNumber || '—'}</Text>
        </View>
        {/* Salary */}
        <View style={[tb.td, { flex: COLS.salary }]}>
          <Text style={tb.cellTxt} numberOfLines={1}>₹{item.salary || 0}</Text>
        </View>
        {/* Rejections */}
        <View style={[tb.td, { flex: COLS.rejections, alignItems: 'center' }]}>
          <Text style={[tb.cellTxt, { fontWeight: '800', color: (item.rejectionCount || 0) > 10 ? C.danger : C.textPrimary }]}>
            {item.rejectionCount ?? 0}
          </Text>
        </View>
        {/* Updated */}
        <View style={[tb.td, { flex: COLS.updated }]}>
          <Text style={tb.cellSub} numberOfLines={2}>{fmtDate(item.updatedAt)}</Text>
        </View>
        {/* Actions: boolean toggles + map + view + delete */}
        <View style={[tb.td, { flex: COLS.actions, flexDirection: 'row', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }]}>
          {TOGGLES.map(t => {
            const on = !!item[t.f];
            return (
              <TouchableOpacity
                key={t.f}
                style={[tb.tgl, { backgroundColor: on ? t.color : '#f1f5f9', borderColor: on ? t.color : C.border }]}
                onPress={() => toggleField(item, t.f)}
              >
                <Text style={[tb.tglTxt, { color: on ? '#fff' : C.textMuted }]}>{t.label}</Text>
              </TouchableOpacity>
            );
          })}
          <TouchableOpacity style={tb.mapBtn} onPress={() => openMap(item)}><Text style={tb.mapTxt}>🗺 Map</Text></TouchableOpacity>
          <TouchableOpacity style={tb.editBtn} onPress={() => setSelected(item)}><Text style={tb.editTxt}>View</Text></TouchableOpacity>
          <TouchableOpacity style={tb.delBtn} onPress={() => setDeleteTarget(item)}><Text style={tb.delTxt}>Delete</Text></TouchableOpacity>
        </View>
      </View>
    );
  };

  const Th = ({ label, flex, align }: { label: string; flex: number; align?: 'center' | 'right' }) => (
    <View style={[tb.th, { flex }, align === 'center' && { alignItems: 'center' }, align === 'right' && { alignItems: 'flex-end' }]}>
      <Text style={tb.thTxt}>{label}</Text>
    </View>
  );

  // ── Detail content (rendered inside the right drawer) ─────────
  const renderDetailContent = () => {
    const r = liveSelected;
    if (!r) return null;
    const dayStats = Object.entries(r.rejectionStats?.day || {}).sort((a, b) => b[0].localeCompare(a[0]));
    const monthStats = Object.entries(r.rejectionStats?.month || {}).sort((a, b) => b[0].localeCompare(a[0]));
    return (
      <View style={{ flex: 1, backgroundColor: '#fff' }}>
        <View style={ds.header}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 }}>
            <View style={[tb.avatar, { backgroundColor: r.active ? C.primary : '#94a3b8' }]}>
              <Text style={tb.avatarTxt}>{vehicleEmoji(r.vehicleType)}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={ds.title} numberOfLines={1}>{r.name || r.email || 'Rider'}</Text>
              <Text style={ds.subtitle}>{r.phone || '—'}</Text>
            </View>
          </View>
          <TouchableOpacity onPress={closeDrawer} style={ds.closeBtn}>
            <Text style={ds.closeBtnText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 40 }} showsVerticalScrollIndicator={false}>
          <View style={ds.pillRow}>
            <View style={[ds.statusPill, { backgroundColor: r.active ? C.successLight : '#f1f5f9' }]}>
              <Text style={[ds.statusPillText, { color: r.active ? C.success : C.textMuted }]}>{r.active ? '● Online' : '○ Offline'}</Text>
            </View>
            <View style={[ds.statusPill, { backgroundColor: r.available ? C.warningLight : '#f1f5f9' }]}>
              <Text style={[ds.statusPillText, { color: r.available ? C.warning : C.textMuted }]}>{r.available ? 'Available' : 'Unavailable'}</Text>
            </View>
            <View style={[ds.statusPill, { backgroundColor: r.profileCompleted ? C.blueLight : C.dangerLight }]}>
              <Text style={[ds.statusPillText, { color: r.profileCompleted ? C.blue : C.danger }]}>{r.profileCompleted ? 'Profile ✓' : 'Profile ✗'}</Text>
            </View>
          </View>

          <Section label="VEHICLE">
            <DRow k="Type" v={r.vehicleType || '—'} />
            <DRow k="Number" v={r.vehicleNumber || '—'} />
          </Section>

          <Section label="MONTHLY SALARY">
            <View style={ds.salaryRow}>
              <View style={ds.salaryInputWrap}>
                <Text style={ds.salaryPrefix}>₹</Text>
                <TextInput
                  style={ds.salaryInput}
                  value={salaryInput}
                  onChangeText={setSalaryInput}
                  keyboardType="numeric"
                  placeholder="0"
                  placeholderTextColor={C.textMuted}
                />
              </View>
              <TouchableOpacity
                style={[ds.salarySaveBtn, savingSalary && { opacity: 0.6 }]}
                onPress={() => saveSalary(r)}
                disabled={savingSalary}
              >
                {savingSalary ? <ActivityIndicator color="#fff" size="small" /> : <Text style={ds.salarySaveTxt}>Save</Text>}
              </TouchableOpacity>
            </View>
          </Section>

          <Section label="IDENTIFIERS">
            <DRow k="Partner ID" v={r.partnerId || r.docId} />
            <DRow k="Email" v={r.email || '—'} />
            <DRow k="Services" v={(r.services || []).join(', ') || '—'} />
            <DRow k="Geohash" v={r.geohash || '—'} />
          </Section>

          <Section label="CURRENT ORDER">
            <DRow k="Current Order" v={r.currentOrderId || '—'} />
            <DRow k="Last Assigned" v={r.lastAssignedOrderId || '—'} />
            <DRow k="Last Assigned At" v={fmtDate(r.lastAssignedAt)} />
          </Section>

          <Section label="LOCATION">
            <DRow k="Latitude" v={r.currentLocation?.latitude != null ? String(r.currentLocation.latitude) : '—'} />
            <DRow k="Longitude" v={r.currentLocation?.longitude != null ? String(r.currentLocation.longitude) : '—'} />
            <DRow k="Updated" v={fmtDate(r.currentLocation?.updatedAt)} />
            {r.currentLocation?.latitude != null && (
              <TouchableOpacity style={ds.mapBtn} onPress={() => openMap(r)}>
                <Text style={ds.mapBtnText}>🗺 Open in Google Maps</Text>
              </TouchableOpacity>
            )}
          </Section>

          <Section label={`REJECTIONS (TOTAL ${r.rejectionCount ?? 0})`}>
            <DRow k="Last Rejected" v={fmtDate(r.lastRejectedAt)} />
            {monthStats.length > 0 && (
              <>
                <Text style={ds.subHead}>By Month</Text>
                {monthStats.map(([k, v]) => <DRow key={k} k={k} v={String(v)} />)}
              </>
            )}
            {dayStats.length > 0 && (
              <>
                <Text style={ds.subHead}>By Day (recent)</Text>
                {dayStats.slice(0, 10).map(([k, v]) => <DRow key={k} k={k} v={String(v)} />)}
              </>
            )}
            {monthStats.length === 0 && dayStats.length === 0 && (
              <Text style={ds.empty}>No rejection history</Text>
            )}
          </Section>

          <Section label="TIMELINE">
            <DRow k="Created" v={fmtDate(r.createdAt)} />
            <DRow k="Updated" v={fmtDate(r.updatedAt)} />
          </Section>

          <View style={ds.actionRow}>
            <TouchableOpacity style={[ds.bigBtn, { backgroundColor: r.active ? C.dangerLight : C.successLight }]} onPress={() => toggleField(r, 'active')}>
              <Text style={[ds.bigBtnText, { color: r.active ? C.danger : C.success }]}>{r.active ? 'Set Offline' : 'Set Online'}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[ds.bigBtn, { backgroundColor: r.available ? '#f1f5f9' : C.warningLight }]} onPress={() => toggleField(r, 'available')}>
              <Text style={[ds.bigBtnText, { color: r.available ? C.textMuted : C.warning }]}>{r.available ? 'Mark Unavailable' : 'Mark Available'}</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Search + filters (filters right of search bar) */}
      <View style={[styles.controls, isMobile && { flexDirection: 'column', alignItems: 'stretch' }]}>
        <View style={[styles.searchContainer, { flex: 1 }]}>
          <Text style={styles.searchIcon}>🔍</Text>
          <TextInput
            style={styles.searchInput}
            placeholder="Search name, phone, vehicle, ID..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor={C.textMuted}
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Text style={{ color: C.textMuted, padding: 4 }}>✕</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        <View style={styles.filterRow}>
          {(['all', 'online', 'available', 'offline'] as const).map(f => (
            <TouchableOpacity
              key={f}
              style={[styles.filterChip, statusFilter === f && styles.filterChipActive]}
              onPress={() => setStatusFilter(f)}
            >
              <Text style={[styles.filterChipText, statusFilter === f && styles.filterChipTextActive]}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Table */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={C.primary} />
          <Text style={styles.loadingText}>Loading riders...</Text>
        </View>
      ) : error ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyIcon}>⚠️</Text>
          <Text style={styles.emptyTitle}>{error}</Text>
        </View>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator style={{ flex: 1 }} contentContainerStyle={{ minWidth: TABLE_MIN_WIDTH, flexGrow: 1 }}>
          <View style={tb.table}>
            <View style={tb.headerRow}>
              <Th label="#" flex={COLS.sn} align="center" />
              <Th label="Rider" flex={COLS.rider} />
              <Th label="Phone" flex={COLS.phone} />
              <Th label="Vehicle" flex={COLS.vehicle} />
              <Th label="Salary" flex={COLS.salary} />
              <Th label="Rej." flex={COLS.rejections} align="center" />
              <Th label="Updated" flex={COLS.updated} />
              <Th label="Actions" flex={COLS.actions} align="right" />
            </View>
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
              {filtered.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyIcon}>🛵</Text>
                  <Text style={styles.emptyTitle}>No riders found</Text>
                  <Text style={styles.emptySubtitle}>Try a different search or filter</Text>
                </View>
              ) : (
                paged.map((item, index) => <Row key={item.docId} item={item} index={index} />)
              )}
            </ScrollView>
          </View>
        </ScrollView>
      )}

      {!loading && !error && filtered.length > 0 && (
        <View style={[styles.bottomBar, isMobile && { flexDirection: 'column', gap: 12 }]}>
          {/* Stats (left) */}
          <View style={[styles.statGroup, isMobile && { flexWrap: 'wrap', justifyContent: 'center' }]}>
            {[
              { label: 'Total', value: stats.total, color: C.textPrimary },
              { label: 'Online', value: stats.active, color: C.success },
              { label: 'Available', value: stats.available, color: C.warning },
              { label: 'On Order', value: stats.onOrder, color: C.blue },
            ].map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 && <View style={styles.statSep} />}
                <View style={styles.statChip}>
                  <Text style={[styles.statChipNum, { color: s.color }]}>{s.value}</Text>
                  <Text style={styles.statChipLabel}>{s.label}</Text>
                </View>
              </React.Fragment>
            ))}
          </View>

          {/* Count + pagination (right) */}
          <View style={styles.pageBtns}>
            <Text style={styles.footerCount}>
              {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
            </Text>
            <TouchableOpacity style={[styles.pageBtn, safePage <= 1 && styles.pageBtnDisabled]} onPress={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}>
              <Text style={[styles.pageBtnTxt, safePage <= 1 && styles.pageBtnTxtDisabled]}>‹ Prev</Text>
            </TouchableOpacity>
            <Text style={styles.pageInfo}>Page {safePage} of {totalPages}</Text>
            <TouchableOpacity style={[styles.pageBtn, safePage >= totalPages && styles.pageBtnDisabled]} onPress={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>
              <Text style={[styles.pageBtnTxt, safePage >= totalPages && styles.pageBtnTxtDisabled]}>Next ›</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* ── Detail (right drawer) ── */}
      <Modal visible={!!selected} animationType="fade" transparent onRequestClose={closeDrawer}>
        <View style={wi.root}>
          <Pressable style={wi.backdrop} onPress={closeDrawer} />
          <Animated.View style={[wi.drawer, { width: drawerW, transform: [{ translateX: slideX }] }]}>
            {renderDetailContent()}
          </Animated.View>
        </View>
      </Modal>

      {/* ── Delete confirm modal (web-safe) ── */}
      <Modal visible={!!deleteTarget} animationType="fade" transparent>
        <Pressable style={cf.overlay} onPress={() => !deleting && setDeleteTarget(null)}>
          <Pressable style={cf.card} onPress={() => {}}>
            <View style={cf.iconWrap}><Text style={{ fontSize: 26 }}>🗑️</Text></View>
            <Text style={cf.title}>Delete rider?</Text>
            <Text style={cf.body}>
              {deleteTarget ? `This permanently deletes "${deleteTarget.name || deleteTarget.phone || 'this rider'}" from ${COLLECTION}. This cannot be undone.` : ''}
            </Text>
            <View style={cf.actions}>
              <TouchableOpacity style={cf.cancelBtn} onPress={() => setDeleteTarget(null)} disabled={deleting}>
                <Text style={cf.cancelTxt}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[cf.confirmBtn, deleting && { opacity: 0.6 }]} onPress={confirmDelete} disabled={deleting}>
                {deleting ? <ActivityIndicator color="#fff" size="small" /> : <Text style={cf.confirmTxt}>Delete</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* ── Toast ── */}
      {toast && (
        <View style={[styles.toast, { backgroundColor: toast.type === 'error' ? C.danger : C.success }]}>
          <Text style={styles.toastTxt}>{toast.type === 'error' ? '✕ ' : '✓ '}{toast.msg}</Text>
        </View>
      )}
    </View>
  );
}

// ── Small helpers for the detail drawer ─────────────────────────
const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <View style={ds.section}>
    <Text style={ds.sectionLabel}>{label}</Text>
    {children}
  </View>
);

const DRow = ({ k, v }: { k: string; v: string }) => (
  <View style={ds.row}>
    <Text style={ds.rowKey}>{k}</Text>
    <Text style={ds.rowVal} numberOfLines={2}>{v}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg, padding: 16 },

  statGroup: { flexDirection: 'row', alignItems: 'center' },
  statChip: { flexDirection: 'row', alignItems: 'baseline', gap: 5, paddingHorizontal: 12 },
  statChipNum: { fontSize: 16, fontWeight: '800' },
  statChipLabel: { fontSize: 11, color: C.textMuted, fontWeight: '600' },
  statSep: { width: 1, height: 16, backgroundColor: C.border },

  controls: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 14 },
  searchContainer: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: C.card,
    borderRadius: 12, borderWidth: 1, borderColor: C.border, paddingHorizontal: 14,
  },
  searchIcon: { fontSize: 14, marginRight: 8 },
  searchInput: { flex: 1, height: 46, fontSize: 14, color: C.textPrimary, outlineStyle: 'none' } as any,
  filterRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  filterChip: {
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20,
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border,
  },
  filterChipActive: { backgroundColor: C.primary, borderColor: C.primary },
  filterChipText: { fontSize: 13, color: C.textMuted, fontWeight: '600' },
  filterChipTextActive: { color: '#fff' },

  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 60 },
  loadingText: { marginTop: 12, color: C.textMuted },

  emptyContainer: { alignItems: 'center', paddingTop: 60 },
  emptyIcon: { fontSize: 48, marginBottom: 12 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: C.textPrimary, marginBottom: 6 },
  emptySubtitle: { fontSize: 14, color: C.textMuted },

  bottomBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: C.border, paddingVertical: 10, paddingHorizontal: 14, marginTop: 8 },
  footerCount: { fontSize: 12, color: C.textMuted, fontWeight: '600' },
  pageBtns: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  pageBtn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 9, borderWidth: 1, borderColor: C.border, backgroundColor: '#fff' },
  pageBtnDisabled: { backgroundColor: '#f1f5f9', borderColor: '#eef2f6' },
  pageBtnTxt: { fontSize: 13, fontWeight: '700', color: C.primary },
  pageBtnTxtDisabled: { color: '#cbd5e1' },
  pageInfo: { fontSize: 13, fontWeight: '600', color: C.textPrimary, minWidth: 96, textAlign: 'center' },

  toast: { position: 'absolute', bottom: 24, alignSelf: 'center', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.2, shadowRadius: 8, elevation: 6 },
  toastTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },
});

// ── Table styles ──────────────────────────────────────────────────
const tb = StyleSheet.create({
  table: { flex: 1, backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: C.border },
  headerRow: { flexDirection: 'row', backgroundColor: '#f1f6f8', borderBottomWidth: 1, borderBottomColor: C.border, paddingHorizontal: 14, paddingVertical: 12 },
  th: { justifyContent: 'center' },
  thTxt: { fontSize: 11, fontWeight: '800', color: C.textMuted, letterSpacing: 0.6, textTransform: 'uppercase' as const },

  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  td: { justifyContent: 'center', paddingRight: 8 },
  snTxt: { fontSize: 12, fontWeight: '700', color: C.textMuted },
  cellTxt: { fontSize: 13, color: C.textPrimary },
  cellSub: { fontSize: 11, color: C.textMuted, marginTop: 1 },

  avatar: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  avatarTxt: { fontSize: 20 },
  name: { fontSize: 14, fontWeight: '600', color: C.textPrimary },
  sub: { fontSize: 11, color: C.textMuted, marginTop: 1 },

  tgl: { borderRadius: 14, borderWidth: 1, paddingHorizontal: 10, paddingVertical: 5 },
  tglTxt: { fontSize: 11, fontWeight: '700' },

  mapBtn: { backgroundColor: '#f1f5f9', borderRadius: 8, paddingVertical: 7, paddingHorizontal: 12, borderWidth: 1, borderColor: C.border },
  mapTxt: { color: C.textMuted, fontWeight: '700', fontSize: 12 },
  editBtn: { backgroundColor: C.primaryLight, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14 },
  editTxt: { color: C.primary, fontWeight: '700', fontSize: 12 },
  delBtn: { backgroundColor: C.dangerLight, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14 },
  delTxt: { color: C.danger, fontWeight: '700', fontSize: 12 },
});

// ── Delete confirm modal styles ───────────────────────────────────
const cf = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: { backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 380, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.2, shadowRadius: 24, elevation: 10 },
  iconWrap: { width: 56, height: 56, borderRadius: 28, backgroundColor: C.dangerLight, justifyContent: 'center', alignItems: 'center', marginBottom: 14 },
  title: { fontSize: 18, fontWeight: '700', color: C.textPrimary, marginBottom: 6 },
  body: { fontSize: 13, color: C.textMuted, textAlign: 'center', lineHeight: 19, marginBottom: 20 },
  actions: { flexDirection: 'row', gap: 10, width: '100%' },
  cancelBtn: { flex: 1, paddingVertical: 11, borderRadius: 10, borderWidth: 1, borderColor: C.border, alignItems: 'center' },
  cancelTxt: { color: C.textPrimary, fontWeight: '600', fontSize: 14 },
  confirmBtn: { flex: 1, paddingVertical: 11, borderRadius: 10, backgroundColor: C.danger, alignItems: 'center' },
  confirmTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },
});

// ── Detail drawer styles ──────────────────────────────────────────
const wi = StyleSheet.create({
  root: { flex: 1, flexDirection: 'row', justifyContent: 'flex-end' },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(15,23,42,0.45)' },
  drawer: { height: '100%', backgroundColor: '#fff', shadowColor: '#000', shadowOffset: { width: -4, height: 0 }, shadowOpacity: 0.18, shadowRadius: 20, elevation: 16 },
});

const ds = StyleSheet.create({
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: 20, borderBottomWidth: 1, borderBottomColor: C.border,
  },
  title: { fontSize: 18, fontWeight: '800', color: C.textPrimary },
  subtitle: { fontSize: 13, color: C.textMuted, marginTop: 2 },
  closeBtn: { width: 32, height: 32, borderRadius: 16, backgroundColor: C.bg, justifyContent: 'center', alignItems: 'center' },
  closeBtnText: { fontSize: 16, color: C.textMuted, fontWeight: '700' },

  pillRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  statusPill: { borderRadius: 10, paddingHorizontal: 12, paddingVertical: 6 },
  statusPillText: { fontSize: 12, fontWeight: '700' },

  section: { marginBottom: 16, paddingBottom: 14, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  sectionLabel: { fontSize: 11, fontWeight: '800', color: '#9CA3AF', letterSpacing: 0.8, marginBottom: 8 },
  subHead: { fontSize: 11, fontWeight: '700', color: C.primary, marginTop: 8, marginBottom: 4 },
  empty: { fontSize: 13, color: C.textMuted, fontStyle: 'italic' },
  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4, gap: 12 },
  rowKey: { fontSize: 13, color: C.textMuted, flexShrink: 0 },
  rowVal: { fontSize: 13, color: C.textPrimary, fontWeight: '600', flex: 1, textAlign: 'right' },

  mapBtn: { marginTop: 10, backgroundColor: C.primaryLight, borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  mapBtnText: { color: C.primary, fontWeight: '700', fontSize: 13 },

  actionRow: { flexDirection: 'row', gap: 10, marginTop: 8 },
  bigBtn: { flex: 1, borderRadius: 12, paddingVertical: 14, alignItems: 'center' },
  bigBtnText: { fontWeight: '700', fontSize: 14 },

  salaryRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  salaryInputWrap: { flex: 1, flexDirection: 'row', alignItems: 'center', height: 44, backgroundColor: '#f8f9fa', borderRadius: 10, borderWidth: 1, borderColor: C.border, paddingHorizontal: 12 },
  salaryPrefix: { fontSize: 15, color: C.textMuted, fontWeight: '700', marginRight: 6 },
  salaryInput: { flex: 1, fontSize: 15, color: C.textPrimary, outlineStyle: 'none' } as any,
  salarySaveBtn: { backgroundColor: C.primary, borderRadius: 10, paddingHorizontal: 22, height: 44, justifyContent: 'center', alignItems: 'center' },
  salarySaveTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },
});
